import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  House,
  Buildings,
  Users,
  Package,
  Receipt,
  SignOut,
  Truck,
  ClockCounterClockwise,
  CreditCard,
  Sun,
  Moon,
  List,
  X,
  MapPin,
  Stack,
  AddressBook,
  PaperPlaneTilt,
  Warehouse,
  HandWaving,
  Lightning,
  Key,
  UserCircle,
  MapTrifold,
  Tag,
  Globe,
  ClipboardText,
  Question,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const NAV = [
  { to: "/dashboard", label: "Dashboard", Icon: House, roles: ["super_admin", "company_admin", "dispatcher", "user", "customer"] },
  { to: "/companies", label: "Companies", Icon: Buildings, roles: ["super_admin"] },
  { to: "/billing", label: "Billing", Icon: CreditCard, roles: ["super_admin", "company_admin"] },
  { to: "/promotions", label: "Promotions", Icon: Tag, roles: ["super_admin"] },
  { to: "/admin/custom-domains", label: "Custom Domains", Icon: Globe, roles: ["super_admin"] },
  { to: "/settings/domain", label: "Custom Domain", Icon: Globe, roles: ["company_admin"] },
  { to: "/zipexpress", label: "ZipExpress", Icon: Lightning, roles: ["super_admin"] },
  { to: "/users", label: "Team", Icon: Users, roles: ["super_admin", "company_admin"] },
  { to: "/recipients", label: "Recipients", Icon: AddressBook, roles: ["super_admin", "company_admin", "dispatcher", "warehouse", "warehouse_super", "warehouse_super_l1", "warehouse_super_l2"] },
  { to: "/packages", label: "Packages", Icon: Package, roles: ["super_admin", "company_admin", "dispatcher", "user", "customer", "warehouse", "warehouse_super", "warehouse_super_l1", "warehouse_super_l2"] },
  { to: "/unclaimed", label: "Unclaimed Packages", Icon: Question, roles: ["super_admin", "company_admin", "dispatcher", "warehouse_super", "warehouse_super_l1", "warehouse_super_l2"] },
  { to: "/pickups", label: "Pickup Requests", Icon: HandWaving, roles: ["super_admin", "company_admin", "dispatcher", "customer"] },
  { to: "/manifests", label: "Manifests", Icon: Stack, roles: ["super_admin", "company_admin", "dispatcher", "user", "warehouse", "warehouse_super", "warehouse_super_l1", "warehouse_super_l2"] },
  { to: "/branches", label: "Branches", Icon: MapPin, roles: ["super_admin", "company_admin", "dispatcher", "user"] },
  { to: "/drivers", label: "Drivers", Icon: Truck, roles: ["super_admin", "company_admin", "dispatcher"] },
  { to: "/driver-assignments", label: "Driver Assignments", Icon: ClipboardText, roles: ["super_admin", "company_admin", "dispatcher"] },
  { to: "/routes", label: "Routes", Icon: MapTrifold, roles: ["super_admin", "company_admin", "dispatcher"] },
  { to: "/live-map", label: "Live Map", Icon: MapPin, roles: ["super_admin"] },
  { to: "/warehouses", label: "Warehouses", Icon: Warehouse, roles: ["super_admin", "company_admin", "dispatcher", "user"] },
  { to: "/invoices", label: "Invoices", Icon: Receipt, roles: ["super_admin", "company_admin", "dispatcher", "user", "customer"] },
  { to: "/communication", label: "Communication", Icon: PaperPlaneTilt, roles: ["super_admin", "company_admin", "dispatcher"] },
  { to: "/audit", label: "Audit Log", Icon: ClockCounterClockwise, roles: ["super_admin", "company_admin"] },
];

const ROLE_LABELS = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  dispatcher: "Dispatcher",
  user: "Viewer",
  customer: "Customer",
  warehouse: "Warehouse",
  warehouse_super: "Kanal Super Warehouse",
  warehouse_super_l1: "Kanal Super Warehouse · L1",
  warehouse_super_l2: "Kanal Super Warehouse · L2",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  // Auto-close drawer on route change
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!user) return null;

  const items = NAV.filter((i) => i.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-[#F4F4F5] dark:bg-zinc-950" data-testid="app-layout">
      {/* Mobile top bar */}
      <div
        className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-14 bg-zinc-950 text-white border-b border-zinc-800"
        data-testid="mobile-topbar"
      >
        <div className="flex items-center gap-2">
          <img src="/brand/kanal-logo.svg" alt="Kanal" className="h-7 w-7 bg-white rounded p-0.5" />
          <div className="font-semibold tracking-tight text-sm" style={{ fontFamily: "Work Sans" }}>
            Kanal
          </div>
        </div>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-md hover:bg-zinc-800 text-zinc-200"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <List size={20} weight="bold" />
        </button>
      </div>

      {/* Backdrop (mobile) */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 bg-zinc-950 text-zinc-100 flex flex-col transform transition-transform duration-200 ease-in-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-zinc-800 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <img src="/brand/kanal-logo.svg" alt="Kanal" className="h-9 w-9 bg-white rounded p-1" />
            <div className="leading-tight">
              <div className="font-semibold tracking-tight text-lg" style={{ fontFamily: "Work Sans" }}>
                Kanal
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                Cyrus Field Tech 
              </div>
            </div>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1.5 rounded-md hover:bg-zinc-800 text-zinc-400"
            data-testid="mobile-close-btn"
            aria-label="Close menu"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/dashboard"}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-zinc-800 text-white"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-zinc-800">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="w-full flex items-center justify-between px-3 py-2 mb-2 rounded-md hover:bg-zinc-900 text-zinc-300 hover:text-white transition-colors"
          >
            <span className="text-xs font-semibold uppercase tracking-wider">Theme</span>
            <span className="flex items-center gap-1.5 text-xs">
              {dark ? <Moon size={14} weight="bold" /> : <Sun size={14} weight="bold" />}
              {dark ? "Dark" : "Light"}
            </span>
          </button>
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              {ROLE_LABELS[user.role]}
            </div>
            <div className="text-sm font-medium truncate" data-testid="user-name">{user.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user.email}</div>
            {user.company_plan && (
              <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">
                {user.company_plan} plan
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/profile")}
            data-testid="my-profile-btn"
            className="w-full justify-start text-zinc-300 hover:text-white hover:bg-zinc-900 mb-1"
          >
            <UserCircle size={16} className="mr-2" />
            My profile
          </Button>
          <Button
            variant="ghost"
            onClick={() => setPwdOpen(true)}
            data-testid="change-password-btn"
            className="w-full justify-start text-zinc-300 hover:text-white hover:bg-zinc-900 mb-1"
          >
            <Key size={16} className="mr-2" />
            Change password
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full justify-start text-zinc-300 hover:text-white hover:bg-zinc-900"
          >
            <SignOut size={16} className="mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />

      {/* Main */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
}
